import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoBack from "../components/GoBack";
import {
  ClaimSummary,
  getClaim,
  payClaim,
  PayClaimResult,
  ClaimInstallmentPlanType,
  isPlanEligible,
  previewPlan,
  PlanPreview,
} from "../api/services";
import { PaymentMethodPicker } from "../components/payment";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { formatCurrency } from "../utils/formatCurrency";

const ALL_PLAN_TYPES: ClaimInstallmentPlanType[] = [
  "pay-in-full",
  "3-month",
  "6-month",
  "12-month",
  "18-month",
];

const usd = formatCurrency;

const ClaimPayment: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [claim, setClaim] = useState<ClaimSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { methods, loading: methodsLoading } = usePaymentMethods();
  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] =
    useState<ClaimInstallmentPlanType>("pay-in-full");
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState<PayClaimResult | null>(null);

  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);

    getClaim(uuid)
      .then((res) => {
        if (!cancelled) setClaim(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
        else {
          console.error("Failed to load claim:", err);
          setError(t("claimPayment.errorLoading"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uuid, t]);

  // Auto-pick the default method, falling back to the first one available.
  useEffect(() => {
    if (selectedMethodId !== null || methods.length === 0) return;
    const def = methods.find((m) => m.isDefault) ?? methods[0];
    if (def) setSelectedMethodId(def.id);
  }, [methods, selectedMethodId]);

  // Available plan previews based on the claim amount. Filters out plans the
  // claim isn't eligible for (12-month requires > $1k, 18-month > $2.5k).
  const planPreviews = useMemo<PlanPreview[]>(() => {
    if (!claim) return [];
    return ALL_PLAN_TYPES.filter((type) =>
      isPlanEligible(type, claim.requestAmount),
    ).map((type) => previewPlan(type, claim.requestAmount));
  }, [claim]);

  // If the user previously picked a plan that's no longer eligible (claim
  // amount changed?), fall back to pay-in-full.
  useEffect(() => {
    if (!planPreviews.length) return;
    if (!planPreviews.some((p) => p.planType === selectedPlan)) {
      setSelectedPlan(planPreviews[0].planType);
    }
  }, [planPreviews, selectedPlan]);

  if (loading) {
    return (
      <div>
        <GoBack to={uuid ? `/claim/${uuid}` : "/dashboard"} />
        <p className="text-sm text-muted">{t("claimPayment.loading")}</p>
      </div>
    );
  }

  if (notFound || !claim) {
    return (
      <div>
        <GoBack to="/dashboard" />
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error || t("claimPayment.notFound")}
        </div>
      </div>
    );
  }

  const policy = claim.policy;
  const selectedPlanPreview =
    planPreviews.find((p) => p.planType === selectedPlan) ?? planPreviews[0];
  const firstChargeAmount = selectedPlanPreview?.monthlyAmount ?? 0;

  const handlePay = async () => {
    if (!selectedMethodId || !selectedPlanPreview) return;
    setPaying(true);
    setError(null);
    try {
      const res = await payClaim(
        claim.uuid,
        selectedMethodId,
        selectedPlanPreview.planType,
      );
      setResult(res.data);
      if (res.data.status === "succeeded") {
        setTimeout(() => navigate(`/claim/${claim.uuid}`), 1500);
      }
    } catch (err: any) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("claimPayment.errorDefault"),
      );
    } finally {
      setPaying(false);
    }
  };

  // Success state
  if (result?.status === "succeeded") {
    return (
      <div>
        <GoBack to={`/claim/${claim.uuid}`} />
        <div className="rounded-xl bg-white shadow-sm">
          <div className="p-10 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
              <svg
                className="h-7 w-7 text-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-primary">
              {t("claimPayment.success.title")}
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {t("claimPayment.success.body", {
                amount: usd(result.amount / 100),
              })}
            </p>
            <p className="mt-1 text-xs text-muted">
              {t("claimPayment.success.redirecting")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Processing state (3DS / pending capture)
  if (result?.status === "processing") {
    return (
      <div>
        <GoBack to={`/claim/${claim.uuid}`} />
        <div className="rounded-xl bg-white shadow-sm">
          <div className="p-10 text-center">
            <h3 className="text-lg font-semibold text-primary">
              {t("claimPayment.processingState.title")}
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {t("claimPayment.processingState.body")}
            </p>
            <button
              type="button"
              className="mt-6 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light cursor-pointer"
              onClick={() => navigate(`/claim/${claim.uuid}`)}
            >
              {t("claimPayment.processingState.back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canPay =
    !!selectedMethodId &&
    !!selectedPlanPreview &&
    !paying &&
    methods.length > 0 &&
    !methodsLoading;

  return (
    <div>
      <GoBack to={`/claim/${claim.uuid}`} />

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {/* By this point in render, succeeded/processing have already
          returned early above. Anything left is a terminal failure state
          (pending/failed/cancelled). */}
      {result && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {result.error ||
            t("claimPayment.errorWithStatus", { status: result.status })}
        </div>
      )}

      {/* Header */}
      <div className="rounded-xl bg-white shadow-sm mb-6">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="font-semibold text-primary">
            {t("claimPayment.headerTitle")}
          </h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-8">
              <h5 className="text-primary mb-2">
                {policy?.unit?.property?.name ?? ""} –{" "}
                {policy?.unit?.name ?? ""}
              </h5>
              {policy?.unit?.property?.address && (
                <p className="text-muted mb-2">
                  {policy.unit.property.address}
                </p>
              )}
              <p>
                <strong>{t("claimDetails.policyLabel")}:</strong>{" "}
                {policy?.name ?? "—"} |{" "}
                <strong>{t("claimPayment.claimLabel")}:</strong>{" "}
                {claim.uuid.slice(0, 8)}…
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <h4 className="text-primary mb-1">
                {t("claimPayment.amountDue", {
                  amount: usd(claim.requestAmount),
                })}
              </h4>
              <small className="text-muted">
                {t("claimPayment.amountSubtitle")}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Plan picker */}
      <div className="rounded-xl bg-white shadow-sm mb-6">
        <div className="border-b border-gray-100 px-5 py-4">
          <h5 className="font-semibold text-primary">
            {t("claimPayment.choosePlan")}
          </h5>
        </div>
        <div className="p-5">
          <div
            role="radiogroup"
            aria-label={t("claimPayment.planAria")}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {planPreviews.map((plan) => {
              const isSelected = selectedPlan === plan.planType;
              const isPayInFull = plan.planType === "pay-in-full";
              return (
                <button
                  key={plan.planType}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  className={`group flex cursor-pointer flex-col rounded-xl border-2 bg-white p-5 text-left transition-all ${
                    isSelected
                      ? "border-primary shadow-md"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedPlan(plan.planType)}
                >
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-primary">
                        {isPayInFull
                          ? t("claimPayment.plan.payInFullTitle")
                          : t("claimPayment.plan.monthsTitle", {
                              count: plan.installments,
                            })}
                      </p>
                      <p className="mt-0.5 text-xs text-muted">
                        {isPayInFull
                          ? t("claimPayment.plan.payInFullSub")
                          : t("claimPayment.plan.monthsSub", {
                              count: plan.installments,
                            })}
                      </p>
                    </div>
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                        isSelected
                          ? "border-primary bg-primary"
                          : "border-gray-300"
                      }`}
                    >
                      {isSelected && (
                        <span className="h-2 w-2 rounded-full bg-white" />
                      )}
                    </span>
                  </div>

                  <div className="mb-4">
                    <p className="text-2xl font-bold text-primary">
                      {usd(plan.monthlyAmount)}
                    </p>
                    <p className="text-xs text-muted">
                      {isPayInFull
                        ? t("claimPayment.plan.totalLabel")
                        : t("claimPayment.plan.perMonth")}
                    </p>
                  </div>

                  <div className="mt-auto space-y-1.5 border-t border-gray-100 pt-3 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted">
                        {t("claimPayment.plan.total")}
                      </span>
                      <span
                        className={`font-semibold ${
                          plan.monthlyFee > 0
                            ? "text-warning"
                            : "text-gray-700"
                        }`}
                      >
                        {usd(plan.totalAmount)}
                      </span>
                    </div>
                    {plan.discountAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted">
                          {t("claimPayment.plan.discount", { percent: 10 })}
                        </span>
                        <span className="font-semibold text-success">
                          −{usd(plan.discountAmount)}
                        </span>
                      </div>
                    )}
                    {plan.monthlyFee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted">
                          {t("claimPayment.plan.monthlyFee")}
                        </span>
                        <span className="font-semibold text-warning">
                          {usd(plan.monthlyFee)}
                        </span>
                      </div>
                    )}
                    {plan.discountAmount === 0 && plan.monthlyFee === 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted">
                          {t("claimPayment.plan.fees")}
                        </span>
                        <span className="font-semibold text-success">
                          {t("claimPayment.plan.noFees")}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {selectedPlanPreview && selectedPlanPreview.installments > 1 && (
            <p className="mt-4 text-xs text-muted">
              {t("claimPayment.plan.firstPaymentNote")}
            </p>
          )}
        </div>
      </div>

      {/* Payment Method */}
      <div className="rounded-xl bg-white shadow-sm mb-6">
        <div className="border-b border-gray-100 px-5 py-4 flex items-center justify-between">
          <h5 className="font-semibold text-primary">
            {t("claimPayment.chooseMethod")}
          </h5>
          <button
            type="button"
            className="text-sm font-medium text-info underline hover:text-sky-600 cursor-pointer"
            onClick={() => navigate("/payment-methods")}
          >
            {t("claimPayment.manageMethods")}
          </button>
        </div>
        <div className="p-5">
          {methodsLoading ? (
            <p className="text-sm text-muted">
              {t("claimPayment.loadingMethods")}
            </p>
          ) : (
            <PaymentMethodPicker
              methods={methods}
              selectedId={selectedMethodId}
              onSelect={setSelectedMethodId}
              emptyMessage={t("claimPayment.emptyMethods")}
            />
          )}
        </div>
      </div>

      {/* Action */}
      <div className="mb-6 flex flex-col items-end gap-2">
        <button
          className="rounded-lg bg-primary px-8 py-3 text-base font-medium text-white transition-colors hover:bg-primary-light cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!canPay}
          onClick={handlePay}
        >
          {paying
            ? t("claimPayment.processing")
            : t("claimPayment.payButton", {
                amount: usd(firstChargeAmount),
              })}
        </button>
        <small className="text-muted">
          {t("claimPayment.payDisclaimer", {
            amount: usd(firstChargeAmount),
          })}
        </small>
      </div>

      {/* Information */}
      <div className="rounded-xl bg-white shadow-sm">
        <div className="p-5">
          <h6 className="text-sm font-semibold">
            {t("claimPayment.securityTitle")}
          </h6>
          <p className="text-sm text-muted">
            {t("claimPayment.securityBody")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClaimPayment;
